import {
  getAPI,
  postAPI,
  deleteAPI,
  postMultipartAPI,
} from "../../../api/Networking";
import {
  InventoryListingDetails,
  ProjectInventoryOpsProductDetails,
  ProjectInventoryOpsProductDetailsForPurchaseOrder,
  ProjectInventoryOpsSummary,
} from "../../../models/ProjectManagement/ProjectInventoryProduct";
import {
  PrintInwardCancelReq,
  PrintInwardQcReq,
  PrintInwardQtyReq,
  PrintInwardTimelineReq,
  PrintOutwardCancelReq,
  PrintOutwardReq,
  RawInwardCancelReq,
  RawInwardQcReq,
  RawInwardQtyReq,
  RawInwardTimelineReq,
  VendorInvoice,
  VendorInvoiceType,
} from "../../../models/ProjectManagement/InventoryOpsRequests";

export default class ProjectInventoryOpsAPI {
  getInventoryOpsProducts = async (
    projectId: number,
  ): Promise<InventoryListingDetails> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProjectInventoryOpsProductDetails = async (
    projectId: number,
    productId: number,
  ): Promise<ProjectInventoryOpsProductDetails> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}/${productId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProjectInventoryOpsProductDetailsForPurchaseOrder = async (
    projectId: number,
    productId: number,
    po_id: number,
  ): Promise<ProjectInventoryOpsProductDetailsForPurchaseOrder> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}/product_id/${productId}/po/${po_id}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateRawInwardQty = async (
    reqBody: RawInwardQtyReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/raw-inward/qty`,
        reqBody,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateRawInwardQc = async (
    body: RawInwardQcReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/raw-inward/qc`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintOutward = async (
    body: PrintOutwardReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/print-outward`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInwardQty = async (
    body: PrintInwardQtyReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/print-inward/qty`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInwardQc = async (
    body: PrintInwardQcReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/print-inward/qc`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  cancelRawInward = async (
    body: RawInwardCancelReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/raw-inward/cancel`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  cancelPrintOutward = async (
    body: PrintOutwardCancelReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/print-outward/cancel`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  cancelPrintInward = async (
    body: PrintInwardCancelReq,
  ): Promise<Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>> => {
    try {
      const response = await postAPI(
        `/v1/project-inventory-ops/id/print-inward/cancel`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateRawInwardExpectedDate = async (
    body: RawInwardTimelineReq,
  ): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-pr-expected-date`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInwardTimeline = async (
    body: PrintInwardTimelineReq,
  ): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-print-inward-timeline`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getSummary = async (
    projectId: number,
    productId: number,
  ): Promise<ProjectInventoryOpsSummary> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}/${productId}/summary`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteRawInwardQCImg = async (id: number): Promise<any> => {
    try {
      const response = await deleteAPI(
        `/v5/projects/inventory-ops/id/raw-inward-qc-image/${id}`,
      );

      return response;
    } catch (error) {
      throw error;
    }
  };
  deletePrintInwardQCImg = async (id: number): Promise<any> => {
    try {
      const response = await deleteAPI(
        `/v5/projects/inventory-ops/id/print-inward-qc-image/${id}`,
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchVendorInvoices = async (
    inwardId: number,
    type: VendorInvoiceType,
  ): Promise<any> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/invoices`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addUpdateVendorInvoices = async (
    inwardId: number,
    type: VendorInvoiceType,
    body: VendorInvoice[],
  ): Promise<any> => {
    try {
      // If any invoice contains files, send as multipart form data
      const hasFiles = body.some(
        (inv) => (inv as any).files && (inv as any).files?.length > 0,
      );
      if (hasFiles) {
        const formData = new FormData();
        // Append a JSON representation of invoices without File objects
        const invoicesWithoutFiles = body.map((inv) => {
          const { files, ...rest } = inv as any;
          return rest;
        });
        formData.append("invoices", JSON.stringify(invoicesWithoutFiles));

        // Append files keyed by invoice_number when available, else by index
        body.forEach((inv, idx) => {
          const files = (inv as any).files as File[] | undefined | null;
          if (files && files.length > 0) {
            const keyBase = (inv as any).invoice_number
              ? String((inv as any).invoice_number)
              : String(idx);
            files.forEach((file) => {
              formData.append(`files[${keyBase}]`, file);
            });
          }
        });

        const response = await postMultipartAPI(
          `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/invoices`,
          formData,
        );
        return response.result;
      } else {
        const formData = new FormData();
        formData.append("invoices", JSON.stringify(body));

        const response = await postAPI(
          `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/invoices`,
          formData,
        );
        return response.result;
      }
    } catch (error) {
      throw error;
    }
  };

  updateInvoiceMarkedAsReceived = async (
    inwardId: number,
    type: VendorInvoiceType,
    markedAsReceived: boolean,
  ): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/mark-received`,
        { mark_received: markedAsReceived },
      );
      return;
    } catch (error) {
      throw error;
    }
  };
}
